<template>
  <div
    class="map-fullscreen-survey-previews"
    :class="{ 'is-embedded': embedded }"
    @mousedown.stop
    @touchstart.stop
    @dblclick.stop
  >
    <div
      v-if="replaceMessage"
      class="map-fullscreen-survey-sheet"
      role="alertdialog"
      aria-live="polite"
    >
      <p class="map-fullscreen-survey-sheet__text">
        {{ replaceMessage }}
      </p>
      <div class="map-fullscreen-survey-sheet__actions">
        <button
          type="button"
          class="map-fullscreen-survey-sheet__btn"
          @click.stop="$emit('cancel-replace')"
        >
          إلغاء
        </button>
        <button
          type="button"
          class="map-fullscreen-survey-sheet__btn map-fullscreen-survey-sheet__btn--danger"
          @click.stop="$emit('confirm-replace')"
        >
          {{ replaceConfirmLabel || 'نعم، تحديث' }}
        </button>
      </div>
    </div>
    <div class="map-fullscreen-survey-previews__row">
      <div
        v-for="item in items"
        :key="`${idPrefix}-${item.key}`"
        class="map-fullscreen-survey-tile"
        :class="{
          'is-active': isActive(item.key),
          'is-loading': isLoading(item.key),
          'is-empty': !hasFile(item.key),
        }"
        :style="item.color ? { '--survey-point-color': item.color } : null"
      >
        <button
          type="button"
          class="map-fullscreen-survey-preview"
          :disabled="isItemDisabled(item.key)"
          :title="previewTitle(item)"
          :aria-label="previewTitle(item)"
          :aria-pressed="isActive(item.key) ? 'true' : 'false'"
          @click.stop="$emit('preview', item.key)"
        >
          <b-spinner
            v-if="isLoading(item.key)"
            small
          />
          <img
            v-else
            :src="item.src"
            :alt="item.label"
          >
        </button>
        <button
          type="button"
          class="map-fullscreen-survey-upload"
          :disabled="busy"
          :title="uploadTitle(item)"
          :aria-label="uploadTitle(item)"
          @click.stop="$emit('upload', item.key)"
        >
          <feather-icon
            icon="UploadIcon"
            size="11"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'MapFullscreenSurveyDock',
  components: {
    BSpinner,
    FeatherIcon,
  },
  props: {
    idPrefix: {
      type: String,
      default: 'survey',
    },
    items: {
      type: Array,
      default: () => [],
    },
    fileFlags: {
      type: Object,
      default: () => ({}),
    },
    activeKey: {
      type: String,
      default: null,
    },
    activeKeys: {
      type: Array,
      default: () => [],
    },
    loadingKey: {
      type: String,
      default: null,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    replaceMessage: {
      type: String,
      default: '',
    },
    replaceConfirmLabel: {
      type: String,
      default: '',
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    hasFile(key) {
      return !!this.fileFlags[key]
    },
    isActive(key) {
      if (Array.isArray(this.activeKeys) && this.activeKeys.includes(key)) return true
      return this.activeKey === key
    },
    isLoading(key) {
      return this.loadingKey === key
    },
    isItemDisabled(key) {
      return this.busy || this.isLoading(key)
    },
    previewTitle(item) {
      if (!item) return ''
      if (!this.hasFile(item.key)) {
        return `لا يوجد ملف ${item.label} — اضغط للرفع`
      }
      if (this.isActive(item.key)) {
        return item.hideTitle || `إخفاء نقاط ${item.label}`
      }
      return item.title || `عرض نقاط ${item.label} على الخريطة`
    },
    uploadTitle(item) {
      if (!item) return 'رفع الملف'
      if (item.uploadTitle) return item.uploadTitle
      return this.hasFile(item.key)
        ? `تحديث ملف ${item.label}`
        : `رفع ملف ${item.label}`
    },
  },
}
</script>

<style lang="scss">
.map-fullscreen-survey-previews {
  display: none;
  position: absolute;
  top: auto;
  bottom: calc(5.65rem + env(safe-area-inset-bottom, 0px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000000;
  max-width: calc(100% - 0.7rem);
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  pointer-events: none;
}

.map-fullscreen-survey-previews.is-embedded {
  display: flex;
  position: relative;
  top: auto;
  right: auto;
  bottom: auto;
  left: auto;
  transform: none;
  z-index: auto;
  max-width: 100%;
  pointer-events: auto;
}

.map-fullscreen-root:fullscreen .map-fullscreen-survey-previews {
  display: flex;
}

.map-fullscreen-root:-webkit-full-screen .map-fullscreen-survey-previews {
  display: flex;
}

.map-fullscreen-survey-previews__row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.28rem;
  max-width: 100%;
  padding: 0.22rem 0.28rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.84);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 6px 20px rgba(10, 51, 69, 0.18);
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  pointer-events: auto;
}

.map-fullscreen-survey-previews__row::-webkit-scrollbar {
  display: none;
}

.map-fullscreen-survey-tile {
  position: relative;
  flex: 0 0 auto;
}

.map-fullscreen-survey-tile::after {
  content: '';
  position: absolute;
  /*! rtl:begin:ignore */
  left: -0.05rem;
  bottom: -0.05rem;
  /*! rtl:end:ignore */
  width: 0.52rem;
  height: 0.52rem;
  border: 1px solid #fff;
  border-radius: 50%;
  background: var(--survey-point-color, transparent);
  box-shadow: 0 0 0 1px rgba(10, 51, 69, 0.18);
  pointer-events: none;
}

.map-fullscreen-survey-preview {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.35rem;
  height: 2.35rem;
  padding: 0.18rem;
  border: 1px solid rgba(10, 51, 69, 0.12);
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(10, 51, 69, 0.08);
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  pointer-events: auto;
}

.map-fullscreen-survey-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 50%;
  pointer-events: none;
}

.map-fullscreen-survey-tile.is-active .map-fullscreen-survey-preview {
  border-color: var(--survey-point-color, #2563eb);
  box-shadow: 0 0 0 2px var(--survey-point-color, rgba(37, 99, 235, 0.45));
}

.map-fullscreen-survey-tile.is-empty .map-fullscreen-survey-preview {
  border-style: dashed;
  opacity: 0.78;
}

.map-fullscreen-survey-preview:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.map-fullscreen-survey-upload {
  position: absolute;
  /*! rtl:begin:ignore */
  right: -0.12rem;
  bottom: -0.12rem;
  /*! rtl:end:ignore */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.05rem;
  height: 1.05rem;
  padding: 0;
  border: 1px solid rgba(10, 51, 69, 0.16);
  border-radius: 50%;
  background: #0a3345;
  color: #fff;
  box-shadow: 0 1px 3px rgba(10, 51, 69, 0.28);
  cursor: pointer;
  pointer-events: auto;
  touch-action: manipulation;
}

.map-fullscreen-survey-upload:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.map-fullscreen-survey-sheet {
  width: min(calc(100vw - 1.25rem), 22rem);
  padding: 0.65rem 0.75rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 10px 28px rgba(10, 51, 69, 0.22);
  pointer-events: auto;
}

.map-fullscreen-survey-sheet__text {
  margin: 0 0 0.55rem;
  font-size: 0.82rem;
  line-height: 1.45;
  color: #0a3345;
}

.map-fullscreen-survey-sheet__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
}

.map-fullscreen-survey-sheet__btn {
  padding: 0.28rem 0.7rem;
  border: 1px solid rgba(10, 51, 69, 0.16);
  border-radius: 8px;
  background: #fff;
  color: #0a3345;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}

.map-fullscreen-survey-sheet__btn--danger {
  border-color: #b45309;
  background: #d97706;
  color: #fff;
}

@media (min-width: 768px) {
  .map-fullscreen-survey-previews:not(.is-embedded) {
    top: max(0.75rem, env(safe-area-inset-top, 0px));
    bottom: auto;
    max-width: min(calc(100% - 12.5rem), 36rem);
  }

  .map-fullscreen-survey-previews__row {
    gap: 0.4rem;
    padding: 0.3rem 0.35rem;
  }

  .map-fullscreen-survey-preview {
    width: 2.75rem;
    height: 2.75rem;
    padding: 0.22rem;
  }

  .map-fullscreen-survey-upload {
    width: 1.15rem;
    height: 1.15rem;
  }
}
</style>
